var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Stack } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StatusPermission } from '../../enum';
import { ROUTER_URL } from '../../router/constants';
import { skipDisabledSteps } from '../../util';
import { useDeleteMultiskill } from '../annual/selectors';
import { TerminologyProvider } from '../app/terminology';
import { ErrorMessageBox, ExitBar, FallbackComponent } from '../common';
import { PageBody } from '../layout/styled';
import { Actions } from './actions';
import { AttritionStep } from './attrition';
import { WizardStep, getWorkflowSteps } from './constants';
import { WizardContext, WizardProvider } from './context';
import { Description } from './description';
import { FinancialStep, PlanCurrencyConversion } from './financial';
import { HandleTimeStep } from './handleTime';
import { WizardLoader } from './loader';
import { ProdHoursStep } from './prodHours';
import { QueuesStep } from './queue';
import { useCompletedStep, useQueueAPI } from './queue/selectors';
import { Review } from './review';
import { useDisabledWizardSteps, useEffectiveCapacityPermission, usePlanAPI, usePlanTypeCategory, useWizardParams, } from './selectors';
import { ShrinkageStep } from './shrinkage';
import { Steps } from './stepper';
import { SupportStaffStep } from './supportStaff';
import { WizardTopActionsSlotContext } from './topActionsSlotContext';
import { WizardSimpleProvider } from './useWizardSimpleContext';
import { WorkVolumeStep } from './workVolume';
import { WorkingDayStep } from './workingDays';
var ComponentByStep = (_a = {},
    _a[WizardStep.Queues] = QueuesStep,
    _a[WizardStep.WorkVolume] = WorkVolumeStep,
    _a[WizardStep.ProdHours] = ProdHoursStep,
    _a[WizardStep.HandleTime] = HandleTimeStep,
    _a[WizardStep.Shrinkage] = ShrinkageStep,
    _a[WizardStep.Attrition] = AttritionStep,
    _a[WizardStep.WorkingDays] = WorkingDayStep,
    _a[WizardStep.SupportStaff] = SupportStaffStep,
    _a[WizardStep.Financial] = FinancialStep,
    _a[WizardStep.Review] = Review,
    _a);
var WizardWrapper = function () {
    var _a, _b;
    var capacityPermission = useEffectiveCapacityPermission();
    var planData = usePlanAPI()[0];
    var _c = useWizardParams(), step = _c.step, stepIndex = _c.stepIndex;
    var Component = ComponentByStep[step];
    var planTypeCategory = usePlanTypeCategory();
    var disabledSteps = useDisabledWizardSteps();
    var isStepDisabled = disabledSteps.includes(step);
    var _d = useSearchParams(), searchParams = _d[0], setSearchParams = _d[1];
    var completedStep = useCompletedStep()[0];
    var _e = useContext(WizardContext), handleSave = _e.handleSave, saveError = _e.saveError, isDirty = _e.methods.formState.isDirty;
    var navigate = useNavigate();
    // Header slot that the Actions component portals a second NEXT/PREVIOUS pair into,
    // so long steps can be navigated without scrolling to the bottom of the page.
    var _f = useState(null), topActionsSlot = _f[0], setTopActionsSlot = _f[1];
    useEffect(function () {
        if (capacityPermission !== StatusPermission.Edit && completedStep !== -1 && completedStep !== WizardStep.Review) {
            navigate(ROUTER_URL.Home);
        }
    }, [completedStep, navigate, capacityPermission]);
    // The plan load is now authorized server-side by the caller's per-client/LOB access. A Forbidden
    // response means the plan belongs to a client/LOB the user may not open (e.g. via a direct URL);
    // bounce them back to their plan list rather than render an empty wizard shell.
    useEffect(function () {
        if (planData.statusCode === HttpStatusCode.Forbidden) {
            navigate(ROUTER_URL.Home);
        }
    }, [planData.statusCode, navigate]);
    // A disabled step reached by URL (an old link or bookmark) moves on to the next enabled step. It
    // waits for the plan: until then the steps follow the Work Volume layout, where a Prod Hours plan's
    // Review index reads as Financial.
    var isPlanLoaded = Boolean(planData.data);
    useEffect(function () {
        if (!isPlanLoaded || !isStepDisabled)
            return;
        var nextStepIndex = skipDisabledSteps(getWorkflowSteps(planTypeCategory), stepIndex, disabledSteps);
        var nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('step', String(nextStepIndex));
        setSearchParams(nextSearchParams, { replace: true });
    }, [isPlanLoaded, isStepDisabled, planTypeCategory, stepIndex, disabledSteps, searchParams, setSearchParams]);
    return (_jsxs(FallbackComponent, __assign({ requesting: planData.requesting !== false && !planData.data }, { children: [_jsx(ErrorMessageBox, { errors: [planData.error, saveError], sx: { margin: 3, marginBottom: 0 } }), _jsx(ExitBar, { title: (_a = planData.data) === null || _a === void 0 ? void 0 : _a.parent, titleEntity: { kind: 'plan', id: (_b = planData.data) === null || _b === void 0 ? void 0 : _b.parentId }, isDirty: isDirty, onSave: handleSave('exit', function () { return navigate(ROUTER_URL.Home); }) }), _jsx(Steps, {}), !planData.error && !isStepDisabled && (_jsx(WizardTopActionsSlotContext.Provider, __assign({ value: topActionsSlot }, { children: _jsxs(PageBody, { children: [_jsxs(Stack, __assign({ direction: "row", justifyContent: "space-between", alignItems: "flex-start", spacing: 4 }, { children: [_jsx(Box, __assign({ sx: { flex: 1, minWidth: 0 } }, { children: _jsx(Description, {}) })), _jsxs(Stack, __assign({ direction: "column", spacing: 4 }, { children: [_jsx(Box, { ref: setTopActionsSlot, sx: { flexShrink: 0 } }), step === WizardStep.Financial && _jsx(PlanCurrencyConversion, {})] }))] })), _jsx(Component, {}), _jsx(Actions, {})] }) })))] })));
};
export var Wizard = function () {
    var _a;
    var _b = useWizardParams(), id = _b.id, step = _b.step, queueId = _b.queueId;
    var planData = usePlanAPI()[0];
    var queueData = useQueueAPI()[0];
    var _c = useDeleteMultiskill(id), deleteMultiskill = _c[1], clearDeleteMultiskill = _c[2];
    var capacityPermission = useEffectiveCapacityPermission();
    useEffect(function () {
        if (capacityPermission === StatusPermission.Edit && id)
            deleteMultiskill();
        return clearDeleteMultiskill;
    }, [deleteMultiskill, clearDeleteMultiskill, id, capacityPermission]);
    return (_jsxs(_Fragment, { children: [_jsx(WizardLoader, {}), _jsx(TerminologyProvider, __assign({ value: (_a = planData.data) === null || _a === void 0 ? void 0 : _a.importSource }, { children: _jsx(WizardSimpleProvider, __assign({ plan: planData.data, queue: queueData.data }, { children: _jsx(WizardProvider, { children: _jsx(WizardWrapper, {}) }, step === WizardStep.Queues ? step : "".concat(step, "-").concat(queueId !== null && queueId !== void 0 ? queueId : 'none')) })) }))] }));
};
